import orderBy from 'lodash/orderBy'
import { useMemo, useState } from 'react'
import { useMarketsData } from './useMarketsData'
import { PoolRisk, getApyTooltipText } from 'modules/pools/constants/PoolRisk'

import { Link } from 'shared/ui/controls/Link'
import { PageLoader } from 'shared/ui/layout/PageLoader'
import { Text } from 'shared/ui/common/Text'
import { formatNumberToLabel } from './utils'
import {
  Table,
  TableRow,
  TableCellHeadData,
  TableSortState,
  TableHeader,
} from 'shared/ui/common/Table'
import { useFirebase } from 'modules/analytics/firebase'
import { DEPOSIT_MARKETS_PAGE_CLICK_EVENT } from 'modules/analytics/events'
import { BorrowLink } from './BorrowLink'
import * as links from 'modules/router/links'
import { useNFCSState } from 'modules/nfcs/hooks/useNFCSState'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'

import { ReactComponent as TokenDAI } from 'assets/token-dai.svg'
import { ReactComponent as TokenUSDC } from 'assets/token-usdc.svg'
import { ReactComponent as TokenUSDT } from 'assets/token-usdt.svg'
import { ReactComponent as TokenETH } from 'assets/token-eth.svg'
import { ReactComponent as TokenWBTC } from 'assets/token-wbtc.svg'
import { ReactComponent as LendWBTC } from 'assets/lend.svg'

import s from './MarketsTable.module.scss'

const icons = {
  DAI: <TokenDAI />,
  USDC: <TokenUSDC />,
  USDT: <TokenUSDT />,
  ETH: <TokenETH />,
  WBTC: <TokenWBTC />,
} as const

const getTableHead: (poolRisk: PoolRisk) => TableCellHeadData[] = poolRisk => [
  {
    sortField: 'asset',
    title: 'Asset',
  },
  {
    sortField: 'totalDeposit',
    title: (
      <>
        Total
        <br />
        Deposited
      </>
    ),
  },
  {
    sortField: 'depositAPY',
    title: (
      <>
        Yield
        <br />
        APy
      </>
    ),
    titleTooltip: getApyTooltipText(poolRisk),
  },
  // {
  //   sortField: 'totalBorrowedAmount',
  //   title: (
  //     <>
  //       total
  //       <br />
  //       borrowed
  //     </>
  //   ),
  // },
  {
    sortField: 'borrowInterest',
    title: (
      <>
        30-days
        <br />
        borrow interest
      </>
    ),
  },
  {
    title: 'Collateral',
    sortField: 'collateralMean',
  },
  {
    title: (
      <>
        Deposit
        <br />
        &#8594;
      </>
    ),
    color: 'colorBranded',
  },
  {
    title: (
      <>
        Borrow
        <br />
        &#8592;
      </>
    ),
    color: 'colorPink',
  },
]

interface Props {
  titlePrimary?: string
  titleSecondary?: string
  poolRisk: PoolRisk
  isEnabledDeposit?: boolean
}

export const MarketsTable: React.FC<Props> = ({
  titlePrimary,
  titleSecondary,
  poolRisk,
  isEnabledDeposit,
}) => {
  const { isWalletConnected } = useWeb3()
  const { nfcsStatus, risk } = useNFCSState()
  const tableHead = useMemo(() => {
    const head = getTableHead(poolRisk)
    if (!isEnabledDeposit) head[5].title = ''
    if (isWalletConnected && (nfcsStatus === 'loading' || risk !== poolRisk))
      head[6].title = ''
    return head
  }, [isEnabledDeposit, poolRisk, risk, nfcsStatus, isWalletConnected])
  const { trackEvent } = useFirebase()

  const [sortState, setSortState] = useState<TableSortState>([
    tableHead[0].sortField ?? null,
    -1,
  ])

  const [sortBy, sortDesc] = sortState

  const { data } = useMarketsData({ poolRisk })

  const sortedData = useMemo(() => {
    if (!sortBy || !data) {
      return data
    }

    return orderBy(data, sortBy, sortDesc === 1 ? 'desc' : 'asc')
  }, [data, sortBy, sortDesc])

  if (!sortedData) {
    return <PageLoader />
  }

  return (
    <>
      {titlePrimary && (
        <div className={s.title}>
          <Text isInLine isUppercased>
            {titlePrimary}{' '}
          </Text>
          {titleSecondary && (
            <Text
              className={s.titleSecondary}
              color="secondary"
              isInLine
              isUppercased
            >
              {titleSecondary}
            </Text>
          )}
        </div>
      )}
      <Table>
        <TableHeader
          headData={tableHead}
          sortState={sortState}
          onChangeSort={setSortState}
          gridTemplateColumns="repeat(5, 2fr) repeat(2, 80px)"
        />

        {sortedData.map((row, i) => (
          <TableRow
            key={i}
            gridTemplateColumns="repeat(5, 2fr) repeat(2, 80px)"
          >
            <div className={s.token}>
              {icons[row.asset]}
              <span>{row.asset}</span>
            </div>
            <span>${formatNumberToLabel(row.totalDeposit)}</span>
            <div>
              <span className={s.faded}>UP TO</span> {row.depositAPY.toFixed(2)}
              %
            </div>
            {/* <div>${formatNumberToLabel(row.totalBorrowedAmount)}</div> */}
            <div>
              <span className={s.faded}>FROM</span>{' '}
              {row.borrowInterest.toFixed(2)}%
            </div>
            <div>{row.collateralRange.join('-')}%</div>
            <div>
              {isEnabledDeposit && (
                <Link
                  to={`${links.deposit}?asset=${row.asset}&risk=${poolRisk}`}
                  testId={`markets-page-deposit-link-${row.asset}-${poolRisk}`}
                  onClick={() =>
                    trackEvent(DEPOSIT_MARKETS_PAGE_CLICK_EVENT, {
                      risk: poolRisk,
                      asset: row.asset,
                    })
                  }
                >
                  <LendWBTC className={s.lendIcon} />
                </Link>
              )}
            </div>
            <BorrowLink asset={row.asset} poolRisk={poolRisk} />
          </TableRow>
        ))}
      </Table>
    </>
  )
}
